<template>
  <div class="transaction-page">
    <div class="transaction-form">
      <!-- 模式切换 -->
      <div class="mode-switcher">
        <div class="mode-tab" :class="{ active: mode === 'manual' }" @click="mode = 'manual'">
          手动记账
        </div>
        <div class="mode-tab" :class="{ active: mode === 'ai' }" @click="mode = 'ai'">
          ✨ AI记账
        </div>
      </div>

      <!-- ── AI 记账模式 ── -->
      <div v-if="mode === 'ai'" class="ai-section">
        <el-form label-position="top" class="ai-form">
          <el-form-item label="记账描述">
            <el-input
              v-model="aiPrompt"
              type="textarea"
              :rows="4"
              maxlength="1000"
              show-word-limit
              placeholder="例如：今天中午用中信卡在满东园吃饭 27 元；昨天收到工资 8000 到招商工资卡"
            />
          </el-form-item>
          <el-form-item label="图片（可选）">
            <el-upload
              :auto-upload="false"
              :limit="6"
              list-type="picture-card"
              :on-change="handleFileChange"
              :on-remove="handleFileRemove"
              :on-preview="handlePreview"
              accept="image/*"
              class="ai-uploader"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
            <el-image-viewer
              v-if="showPreview"
              :url-list="previewList"
              :initial-index="previewIndex"
              @close="showPreview = false"
            />
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              :loading="analyzing"
              :disabled="!canAnalyze"
              style="width:100%"
              @click="handleAnalyze"
            >
              {{ analyzing ? 'AI 分析中...' : 'AI 生成草稿' }}
            </el-button>
          </el-form-item>
        </el-form>

        <!-- 草稿列表 -->
        <div v-if="drafts.length" class="ai-drafts">
          <div class="drafts-header">
            <span>交易草稿（{{ drafts.length }} 笔）</span>
            <div style="display:flex;gap:8px">
              <el-button size="small" @click="drafts = []">清除</el-button>
              <el-button type="success" size="small" :loading="saving" @click="handleSaveAll">确认写入</el-button>
            </div>
          </div>
          <div v-for="(draft, index) in drafts" :key="index" class="draft-item">
            <div class="draft-top">
              <strong>#{{ index + 1 }}</strong>
              <el-tag :type="draft.transaction_type === '收入' ? 'success' : draft.transaction_type === '支出' ? 'danger' : 'info'" size="small">
                {{ draft.transaction_type }}
              </el-tag>
              <span class="confidence">{{ Math.round((draft.confidence || 0) * 100) }}%</span>
            </div>
            <el-form label-position="top" class="draft-form">
              <el-form-item label="时间">
                <el-date-picker v-model="draft.transaction_date" type="datetime" value-format="YYYY-MM-DD HH:mm:ss" format="YYYY-MM-DD HH:mm" style="width:100%" />
              </el-form-item>
              <el-form-item label="类型">
                <el-select v-model="draft.transaction_type" style="width:100%">
                  <el-option label="支出" value="支出" /><el-option label="收入" value="收入" />
                  <el-option label="转账" value="转账" /><el-option label="借入" value="借入" />
                  <el-option label="借出" value="借出" /><el-option label="收款" value="收款" />
                  <el-option label="还款" value="还款" />
                </el-select>
              </el-form-item>
              <el-form-item label="金额">
                <el-input-number v-model="draft.amount" :precision="2" :min="0.01" style="width:100%" />
              </el-form-item>
              <el-form-item label="主账户" class="draft-full">
                <AccountSelect v-model="draft.primary_account_id" include-hidden placeholder="选择账户" style="width:100%" />
              </el-form-item>
              <el-form-item v-if="needsSecondaryAccount(draft)" label="次账户" class="draft-full">
                <AccountSelect v-model="draft.secondary_account_id" include-hidden placeholder="选择账户" style="width:100%" />
              </el-form-item>
              <el-form-item v-if="draft.transaction_type === '支出'" label="支出大类">
                <el-select v-model="draft.expense_major_category_id" filterable clearable style="width:100%">
                  <el-option v-for="c in expenseMajorCategories" :key="c.id" :label="c.category_name" :value="c.id" />
                </el-select>
              </el-form-item>
              <el-form-item v-if="draft.transaction_type === '支出'" label="支出小类">
                <el-select v-model="draft.expense_minor_category_id" filterable clearable style="width:100%">
                  <el-option v-for="c in expenseMinorByMajor(draft.expense_major_category_id)" :key="c.id" :label="c.category_name" :value="c.id" />
                </el-select>
              </el-form-item>
              <el-form-item v-if="draft.transaction_type === '收入'" label="收入大类">
                <el-select v-model="draft.income_major_category_id" filterable clearable style="width:100%">
                  <el-option v-for="c in incomeMajorCategories" :key="c.id" :label="c.category_name" :value="c.id" />
                </el-select>
              </el-form-item>
              <el-form-item label="备注">
                <el-input v-model="draft.description" type="textarea" :rows="2" />
              </el-form-item>
            </el-form>
            <el-alert v-if="draft.reason" type="info" :closable="false" :title="draft.reason" style="margin-top:8px" />
          </div>
        </div>
      </div>

      <!-- ── 手动记账模式 ── -->
      <template v-if="mode === 'manual'">

      <!-- 顶部选项卡 -->
      <div class="tab-header">
        <div class="tab-nav">
          <div 
            v-for="tab in tabs" 
            :key="tab.value"
            class="tab-item"
            :class="{ active: activeTab === tab.value }"
            @click="switchTab(tab.value)"
          >
            {{ tab.label }}
          </div>
        </div>
      </div>

      <!-- 金额显示区域 -->
      <div class="amount-section">
        <div class="amount-display">
          <span class="currency">¥</span>
          <span class="amount-number">{{ displayAmount }}</span>
        </div>
        <!-- 当前选择的分类 -->
        <div class="current-category" v-if="selectedMajorCategory">
          <div class="category-tag">
            <span class="category-name">{{ selectedMajorCategory.category_name }}</span>
            <span class="subcategory-name" v-if="selectedMinorCategory"> / {{ selectedMinorCategory.category_name }}</span>
            <el-icon class="clear-icon" @click.stop="clearCategory"><Close /></el-icon>
          </div>
        </div>
      </div>

      <!-- 中间内容区域 -->
      <div class="main-content">
        <!-- 分类选择区域 -->
        <div class="category-section" v-if="!selectedMajorCategory || showCategoryPicker">
          <div class="section-header" v-if="selectedMajorCategory && selectedMajorCategory.children?.length > 0">
            <el-button link @click="backToMajorCategories" class="back-btn">
              <el-icon><ArrowLeft /></el-icon>
              返回
            </el-button>
            <span class="section-title">{{ selectedMajorCategory.category_name }}</span>
          </div>
          <div class="section-title" v-else>选择分类</div>
          
          <!-- 搜索框 -->
          <div class="search-box">
            <el-input
              v-model="searchQuery"
              placeholder="搜索分类..."
              clearable
              :prefix-icon="Search"
              size="small"
            />
          </div>
          
          <!-- 搜索结果 -->
          <div class="category-grid" v-if="searchQuery && filteredCategories.length > 0">
            <div 
              v-for="category in filteredCategories" 
              :key="category.id + '-' + (category.parent_id || 'major')"
              class="category-item"
              @click="selectSearchResult(category)"
            >
              <div class="category-icon" :style="{ backgroundColor: getCategoryColor(category.category_name) }">
                <el-icon><component :is="getCategoryIcon(category.category_name)" /></el-icon>
              </div>
              <span class="category-label">{{ category.category_name }}</span>
              <span class="category-parent" v-if="category.parent_name">{{ category.parent_name }}</span>
            </div>
          </div>
          
          <!-- 无搜索结果 -->
          <div class="no-result" v-else-if="searchQuery && filteredCategories.length === 0">
            <span>未找到匹配的分类</span>
          </div>
          
          <!-- 分类网格 -->
          <div class="category-grid" v-else>
            <template v-if="!selectedMajorCategory || !selectedMajorCategory.children?.length">
              <div 
                v-for="category in currentMajorCategories" 
                :key="category.id"
                class="category-item"
                @click="selectMajorCategory(category)"
              >
                <div class="category-icon" :style="{ backgroundColor: getCategoryColor(category.category_name) }">
                  <el-icon><component :is="getCategoryIcon(category.category_name)" /></el-icon>
                </div>
                <span class="category-label">{{ category.category_name }}</span>
              </div>
            </template>
            <template v-else>
              <div 
                v-for="category in selectedMajorCategory.children" 
                :key="category.id"
                class="category-item"
                :class="{ selected: selectedMinorCategory?.id === category.id }"
                @click="selectMinorCategory(category)"
              >
                <div class="category-icon" :style="{ backgroundColor: getCategoryColor(category.category_name) }">
                  <el-icon><component :is="getCategoryIcon(category.category_name)" /></el-icon>
                </div>
                <span class="category-label">{{ category.category_name }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- 表单区域 -->
        <div class="form-section" v-else>
          <div class="form-row">
            <label>{{ activeTab === 'transfer' ? '转出账户' : '账户' }}</label>
            <AccountSelect
              v-model="transactionForm.primary_account_id"
              :placeholder="activeTab === 'transfer' ? '选择转出账户' : '选择账户'"
              class="form-select"
            />
          </div>

          <div class="form-row" v-if="activeTab === 'transfer'">
            <label>转入账户</label>
            <AccountSelect
              v-model="transactionForm.secondary_account_id"
              placeholder="选择转入账户"
              class="form-select"
            />
          </div>
          
          <div class="form-row">
            <label>时间</label>
            <el-date-picker
              v-model="transactionForm.transaction_date"
              type="datetime"
              placeholder="选择时间"
              format="YYYY-MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm:ss"
              class="form-date"
            />
          </div>
          
          <div class="form-row">
            <label>备注</label>
            <el-input
              v-model="transactionForm.description"
              placeholder="输入备注..."
              class="form-input"
            />
          </div>
          
          <div class="change-category" @click="showCategoryPicker = true">
            <el-icon><Edit /></el-icon>
            <span>更换分类</span>
          </div>
        </div>
      </div>

      <!-- 数字键盘 -->
      <div class="number-keypad">
        <div class="keypad-grid">
          <button class="key-btn number" @click="inputNumber('1')">1</button>
          <button class="key-btn number" @click="inputNumber('2')">2</button>
          <button class="key-btn number" @click="inputNumber('3')">3</button>
          <button class="key-btn delete" @click="deleteNumber">
            <el-icon><Delete /></el-icon>
          </button>

          <button class="key-btn number" @click="inputNumber('4')">4</button>
          <button class="key-btn number" @click="inputNumber('5')">5</button>
          <button class="key-btn number" @click="inputNumber('6')">6</button>
          <button class="key-btn operation" @click="clearAll">清空</button>

          <button class="key-btn number" @click="inputNumber('7')">7</button>
          <button class="key-btn number" @click="inputNumber('8')">8</button>
          <button class="key-btn number" @click="inputNumber('9')">9</button>
          <button class="key-btn operation" @click="inputNumber('+')">+</button>

          <button class="key-btn number" @click="inputNumber('.')">.</button>
          <button class="key-btn number" @click="inputNumber('0')">0</button>
          <button class="key-btn operation" @click="inputNumber('-')">-</button>
          <button class="key-btn save" @click="saveTransaction">保存</button>
        </div>
      </div>

      </template><!-- end manual mode -->
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { ElMessage, ElImageViewer } from 'element-plus'
import {
  Delete, ArrowLeft, Close, Edit, Search, Restaurant, Car, ShoppingBag, House, Iphone,
  Money, TrendCharts, Present, CreditCard, Wallet, School,
  Coffee, FirstAidKit as Medicine, Plus
} from '@element-plus/icons-vue'
import api from '../api'
import dayjs from 'dayjs'
import AccountSelect from './AccountSelect.vue'

export default {
  name: 'TransactionForm',
  components: {
    Delete, ArrowLeft, Close, Edit, Search, Restaurant, Car, ShoppingBag, House, Iphone,
    Money, TrendCharts, Present, CreditCard, Wallet, School, Coffee, Medicine, Plus, AccountSelect,
    ElImageViewer
  },
  setup() {
    const store = useStore()
    const route = useRoute()

    // ── 模式切换 ──────────────────────────────────────────────
    const mode = ref(route.query.mode === 'ai' ? 'ai' : 'manual')
    watch(() => route.query.mode, (val) => { mode.value = val === 'ai' ? 'ai' : 'manual' })

    // ── AI 记账状态 ───────────────────────────────────────────
    const aiPrompt = ref('')
    const aiFiles = ref([])
    const drafts = ref([])
    const analyzing = ref(false)
    const saving = ref(false)
    const expenseMajorCategories = ref([])
    const expenseMinorCategories = ref([])
    const incomeMajorCategories = ref([])

    const canAnalyze = computed(() => aiPrompt.value.trim() || aiFiles.value.length)

    // 图片预览
    const showPreview = ref(false)
    const previewList = ref([])
    const previewIndex = ref(0)
    const uploadFileList = ref([])

    const handleFileChange = (file, fileList) => {
      uploadFileList.value = fileList
      aiFiles.value = fileList.map(item => item.raw).filter(Boolean)
    }
    const handleFileRemove = (file, fileList) => {
      uploadFileList.value = fileList
      aiFiles.value = fileList.map(item => item.raw).filter(Boolean)
    }
    const handlePreview = (file) => {
      previewList.value = uploadFileList.value.map(f => f.url).filter(Boolean)
      previewIndex.value = uploadFileList.value.findIndex(f => f.uid === file.uid)
      showPreview.value = true
    }

    const handleAnalyze = async () => {
      analyzing.value = true
      try {
        const formData = new FormData()
        formData.append('prompt', aiPrompt.value || '请根据图片生成记账草稿')
        aiFiles.value.forEach(file => formData.append('images', file))
        const response = await api.analyzeAIAccounting(formData)
        drafts.value = response.data.transactions || []
        if (!drafts.value.length) {
          ElMessage.warning('AI没有识别出可记账交易')
        } else {
          ElMessage.success(`生成 ${drafts.value.length} 笔草稿，请确认后写入`)
        }
      } finally {
        analyzing.value = false
      }
    }

    const toTransactionPayload = (draft) => ({
      transaction_date: draft.transaction_date,
      transaction_type: draft.transaction_type,
      amount: draft.amount,
      primary_account_id: draft.primary_account_id,
      secondary_account_id: draft.secondary_account_id || null,
      expense_major_category_id: draft.transaction_type === '支出' ? draft.expense_major_category_id || null : null,
      expense_minor_category_id: draft.transaction_type === '支出' ? draft.expense_minor_category_id || null : null,
      income_major_category_id: draft.transaction_type === '收入' ? draft.income_major_category_id || null : null,
      description: draft.description || '',
      merchant_id: 1, tag_id: 1, member_id: 1, member_amount: draft.amount, reimbursement_status_id: 1
    })

    const handleSaveAll = async () => {
      saving.value = true
      try {
        for (const draft of drafts.value) {
          await api.createTransaction(toTransactionPayload(draft))
        }
        ElMessage.success(`已写入 ${drafts.value.length} 笔交易`)
        drafts.value = []
        aiPrompt.value = ''
        aiFiles.value = []
      } finally {
        saving.value = false
      }
    }

    const needsSecondaryAccount = (draft) =>
      ['转账', '借入', '借出', '收款', '还款'].includes(draft.transaction_type)

    const expenseMinorByMajor = (majorId) => {
      if (!majorId) return expenseMinorCategories.value
      return expenseMinorCategories.value.filter(
        item => item.parent_id === majorId || item.major_category_id === majorId
      )
    }

    // ── 手动记账状态 ──────────────────────────────────────────
    const activeTab = ref('income')
    const amountInput = ref('0.00')
    const selectedMajorCategory = ref(null)
    const selectedMinorCategory = ref(null)
    const showCategoryPicker = ref(false)
    const expenseCategories = ref([])
    const incomeCategories = ref([])
    const searchQuery = ref('')
    
    // 搜索过滤后的分类
    const filteredCategories = computed(() => {
      if (!searchQuery.value.trim()) return []
      
      const query = searchQuery.value.toLowerCase()
      const results = []
      const categories = activeTab.value === 'expense' ? expenseCategories.value : incomeCategories.value
      
      categories.forEach(major => {
        // 搜索大类
        if (major.category_name.toLowerCase().includes(query)) {
          results.push({ ...major, parent_name: null })
        }
        // 搜索小类
        if (major.children) {
          major.children.forEach(minor => {
            if (minor.category_name.toLowerCase().includes(query)) {
              results.push({ ...minor, parent_name: major.category_name, parent_id: major.id })
            }
          })
        }
      })
      
      return results.slice(0, 12) // 限制显示数量
    })
    
    const selectSearchResult = (category) => {
      if (category.parent_id) {
        // 选择的是小类
        const parent = (activeTab.value === 'expense' ? expenseCategories.value : incomeCategories.value)
          .find(c => c.id === category.parent_id)
        selectedMajorCategory.value = parent
        selectedMinorCategory.value = category
      } else {
        // 选择的是大类
        selectedMajorCategory.value = category
        selectedMinorCategory.value = null
      }
      searchQuery.value = ''
      showCategoryPicker.value = false
      updateFormCategory()
    }
    
    const transactionForm = reactive({
      transaction_type: '收入',
      amount: 0,
      primary_account_id: null,
      secondary_account_id: null,
      transaction_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      expense_major_category_id: null,
      expense_minor_category_id: null,
      income_major_category_id: null,
      description: ''
    })

    const tabs = [
      { label: '收入', value: 'income' },
      { label: '支出', value: 'expense' },
      { label: '转账', value: 'transfer' },
      { label: '借贷', value: 'loan' }
    ]

    const accounts = computed(() => store.state.accounts)
    
    const currentMajorCategories = computed(() => {
      switch (activeTab.value) {
        case 'income':
          return incomeCategories.value
        case 'expense':
          return expenseCategories.value
        case 'transfer':
          return [{ id: 1, category_name: '转账', children: [] }]
        case 'loan':
          return [
            { id: 'receivable', category_name: '收款', children: [] },
            { id: 'repayment', category_name: '还款', children: [] },
            { id: 'lending', category_name: '借出', children: [] },
            { id: 'borrowing', category_name: '借入', children: [] }
          ]
        default:
          return []
      }
    })

    const displayAmount = computed(() => {
      if (amountInput.value === '0.00' || amountInput.value === '') {
        return '0.00'
      }
      try {
        if (amountInput.value.includes('+') || amountInput.value.includes('-')) {
          const result = Function(`"use strict"; return (${amountInput.value})`)()
          return parseFloat(result).toFixed(2)
        }
        return parseFloat(amountInput.value).toFixed(2)
      } catch {
        return amountInput.value
      }
    })

    const getCategoryIcon = (categoryName) => {
      const iconMap = {
        '餐饮': 'Restaurant', '交通': 'Car', '购物': 'ShoppingBag',
        '居住': 'House', '通讯': 'Iphone', '娱乐': 'Present',
        '医疗': 'Medicine', '教育': 'School', '金融': 'CreditCard',
        '其他': 'Wallet', '工资薪水': 'Money', '投资收益': 'TrendCharts',
        '兼职收入': 'Wallet', '奖金红包': 'Present', '经营收入': 'Money',
        '转账收入': 'CreditCard', '其他收入': 'Money', '转账': 'CreditCard',
        '收款': 'Money', '还款': 'CreditCard', '借出': 'Wallet', '借入': 'TrendCharts'
      }
      return iconMap[categoryName] || 'Wallet'
    }

    const getCategoryColor = (categoryName) => {
      const colorMap = {
        '餐饮': '#ff6b6b', '交通': '#4ecdc4', '购物': '#45b7d1',
        '居住': '#96ceb4', '通讯': '#ffeaa7', '娱乐': '#fd79a8',
        '医疗': '#e17055', '教育': '#a29bfe', '金融': '#636e72',
        '其他': '#74b9ff', '工资薪水': '#00b894', '投资收益': '#0984e3',
        '兼职收入': '#6c5ce7', '奖金红包': '#e17055', '经营收入': '#00cec9',
        '转账收入': '#636e72', '其他收入': '#74b9ff', '转账': '#636e72',
        '收款': '#00b894', '还款': '#e17055', '借出': '#6c5ce7', '借入': '#a29bfe'
      }
      return colorMap[categoryName] || '#74b9ff'
    }

    const switchTab = (tab) => {
      activeTab.value = tab
      selectedMajorCategory.value = null
      selectedMinorCategory.value = null
      showCategoryPicker.value = false
      amountInput.value = '0.00'
      searchQuery.value = ''
      
      const typeMap = { 'income': '收入', 'expense': '支出', 'transfer': '转账', 'loan': '借入' }
      transactionForm.transaction_type = typeMap[tab]
    }

    const selectMajorCategory = (category) => {
      selectedMajorCategory.value = category
      selectedMinorCategory.value = null
      showCategoryPicker.value = false
      
      if (!category.children || category.children.length === 0) {
        updateFormCategory()
      }
    }

    const selectMinorCategory = (category) => {
      selectedMinorCategory.value = category
      showCategoryPicker.value = false
      updateFormCategory()
    }

    const updateFormCategory = () => {
      if (activeTab.value === 'expense') {
        transactionForm.expense_major_category_id = selectedMajorCategory.value?.id
        transactionForm.expense_minor_category_id = selectedMinorCategory.value?.id
      } else if (activeTab.value === 'income') {
        transactionForm.income_major_category_id = selectedMajorCategory.value?.id
      }
    }

    const backToMajorCategories = () => {
      selectedMajorCategory.value = null
      selectedMinorCategory.value = null
    }

    const clearCategory = () => {
      selectedMajorCategory.value = null
      selectedMinorCategory.value = null
      showCategoryPicker.value = false
    }

    const inputNumber = (value) => {
      if (amountInput.value === '0.00' || amountInput.value === '0') {
        if (value === '.') {
          amountInput.value = '0.'
        } else if (!['+', '-'].includes(value)) {
          amountInput.value = value
        } else {
          amountInput.value += value
        }
      } else {
        amountInput.value += value
      }
    }

    const deleteNumber = () => {
      if (amountInput.value.length > 1) {
        amountInput.value = amountInput.value.slice(0, -1)
      } else {
        amountInput.value = '0.00'
      }
    }

    const clearAll = () => {
      amountInput.value = '0.00'
    }

    const getTransactionType = () => {
      switch (activeTab.value) {
        case 'income': return '收入'
        case 'expense': return '支出'
        case 'transfer': return '转账'
        case 'loan': return selectedMajorCategory.value?.category_name || '借贷'
        default: return '其他'
      }
    }

    const saveTransaction = async () => {
      try {
        let finalAmount = 0
        try {
          if (amountInput.value.includes('+') || amountInput.value.includes('-')) {
            finalAmount = Function(`"use strict"; return (${amountInput.value})`)()
          } else {
            finalAmount = parseFloat(amountInput.value)
          }
        } catch {
          ElMessage.error('请输入有效的金额')
          return
        }

        if (finalAmount <= 0) {
          ElMessage.error('金额必须大于0')
          return
        }

        if (!selectedMajorCategory.value && activeTab.value !== 'transfer') {
          ElMessage.error('请选择分类')
          return
        }

        if (!transactionForm.primary_account_id) {
          ElMessage.error(activeTab.value === 'transfer' ? '请选择转出账户' : '请选择账户')
          return
        }

        if (activeTab.value === 'transfer' && !transactionForm.secondary_account_id) {
          ElMessage.error('请选择转入账户')
          return
        }

        if (activeTab.value === 'transfer' && transactionForm.primary_account_id === transactionForm.secondary_account_id) {
          ElMessage.error('转出账户和转入账户不能相同')
          return
        }

        const requestData = {
          ...transactionForm,
          amount: finalAmount,
          transaction_type: getTransactionType()
        }

        await api.createTransaction(requestData)
        ElMessage.success('交易记录创建成功')
        resetForm()
        
      } catch (error) {
        console.error('保存交易记录失败:', error)
        ElMessage.error('保存失败，请重试')
      }
    }

    const resetForm = () => {
      amountInput.value = '0.00'
      selectedMajorCategory.value = null
      selectedMinorCategory.value = null
      showCategoryPicker.value = false
      Object.assign(transactionForm, {
        amount: 0,
        primary_account_id: null,
        secondary_account_id: null,
        transaction_date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        expense_major_category_id: null,
        expense_minor_category_id: null,
        income_major_category_id: null,
        description: ''
      })
    }

    const loadCategories = async () => {
      try {
        const [expenseData, incomeData] = await Promise.all([
          api.getExpenseCategoriesTree(),
          api.getIncomeCategoriesTree()
        ])
        expenseCategories.value = expenseData
        incomeCategories.value = incomeData
      } catch (error) {
        console.error('加载分类数据失败:', error)
      }
    }

    onMounted(async () => {
      await store.dispatch('fetchAccounts', { includeHidden: true })
      loadCategories()

      // AI 分类
      const [expenseMajor, expenseMinor, incomeMajor] = await Promise.all([
        api.getExpenseMajorCategories(),
        api.getExpenseMinorCategories(),
        api.getIncomeMajorCategories()
      ])
      expenseMajorCategories.value = expenseMajor || []
      expenseMinorCategories.value = expenseMinor || []
      incomeMajorCategories.value = incomeMajor || []

      // 从 URL 参数获取账户 ID 并自动选择
      const accountId = route.query.account_id
      if (accountId) {
        transactionForm.primary_account_id = parseInt(accountId)
      }
    })

    return {
      mode,
      // AI
      aiPrompt, aiFiles, drafts, analyzing, saving,
      canAnalyze, expenseMajorCategories, incomeMajorCategories,
      showPreview, previewList, previewIndex, handlePreview,
      handleFileChange, handleFileRemove, handleAnalyze, handleSaveAll,
      needsSecondaryAccount, expenseMinorByMajor,
      // 手动
      activeTab, tabs, amountInput, displayAmount,
      selectedMajorCategory, selectedMinorCategory, showCategoryPicker,
      currentMajorCategories, transactionForm, accounts,
      searchQuery, filteredCategories, Search,
      getCategoryIcon, getCategoryColor,
      switchTab, selectMajorCategory, selectMinorCategory,
      backToMajorCategories, clearCategory, selectSearchResult,
      inputNumber, deleteNumber, clearAll, saveTransaction
    }
  }
}
</script>

<style scoped>
/* 模式切换 */
.mode-switcher {
  display: flex;
  padding: 12px 16px 0;
  gap: 0;
  flex-shrink: 0;
}

.mode-tab {
  flex: 1;
  text-align: center;
  padding: 10px 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.mode-tab.active {
  color: #fff;
  border-bottom-color: #fff;
}

/* AI 记账区域 */
.ai-section {
  flex: 1;
  background: #fff;
  border-radius: 20px 20px 0 0;
  overflow-y: auto;
  padding: 20px 16px;
}

.ai-form :deep(.el-form-item__label) {
  font-size: 13px;
  color: #666;
  padding-bottom: 4px;
}

.ai-uploader :deep(.el-upload--picture-card) {
  width: 72px;
  height: 72px;
}

.ai-uploader :deep(.el-upload-list__item) {
  width: 72px;
  height: 72px;
}

.ai-drafts {
  margin-top: 4px;
}

.drafts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  padding: 8px 0 12px;
  border-top: 1px solid #f0f2f5;
}

.draft-item {
  padding: 14px;
  border: 1px solid #e5eaf3;
  border-radius: 12px;
  margin-bottom: 12px;
  background: #fafbff;
}

.draft-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.confidence {
  font-size: 12px;
  color: #909399;
  margin-left: auto;
}

.draft-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.draft-form :deep(.el-form-item) {
  margin-bottom: 0;
}

.draft-full {
  grid-column: 1 / -1;
}

@media (max-width: 480px) {
  .draft-form {
    grid-template-columns: 1fr;
  }
}

/* 页面容器 - 桌面端居中显示 */
.transaction-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
}

/* 表单主容器 - 模拟手机屏幕 */
.transaction-form {
  width: 100%;
  max-width: 420px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
}

/* 顶部选项卡 */
.tab-header {
  padding: 16px 16px 0;
  flex-shrink: 0;
}

.tab-nav {
  display: flex;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 4px;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 10px 8px;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.tab-item.active {
  background: #fff;
  color: #667eea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* 金额显示区域 */
.amount-section {
  padding: 24px 20px 16px;
  text-align: center;
  flex-shrink: 0;
}

.amount-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  margin-bottom: 12px;
}

.currency {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.amount-number {
  font-size: 48px;
  font-weight: bold;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif;
  letter-spacing: -1px;
}

.current-category {
  display: flex;
  justify-content: center;
}

.category-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  color: white;
}

.category-tag .category-name {
  font-weight: 500;
}

.category-tag .subcategory-name {
  opacity: 0.9;
}

.category-tag .clear-icon {
  margin-left: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.category-tag .clear-icon:hover {
  opacity: 1;
}

/* 中间内容区域 */
.main-content {
  flex: 1;
  background: #fff;
  border-radius: 20px 20px 0 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* 分类选择区域 */
.category-section {
  padding: 20px 16px;
  flex: 1;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.back-btn {
  color: #667eea;
  font-size: 14px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.section-header .section-title {
  margin-bottom: 0;
  margin-left: 8px;
}

.search-box {
  margin-bottom: 16px;
}

.search-box :deep(.el-input__wrapper) {
  border-radius: 10px;
  background: #f5f5f5;
  box-shadow: none;
}

.search-box :deep(.el-input__wrapper):hover,
.search-box :deep(.el-input__wrapper.is-focus) {
  box-shadow: none;
  background: #eee;
}

.no-result {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px 12px;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s;
}

.category-item:active {
  transform: scale(0.95);
}

.category-item.selected .category-icon {
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
}

.category-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 22px;
  margin-bottom: 8px;
  transition: box-shadow 0.2s;
}

.category-label {
  font-size: 12px;
  color: #666;
  text-align: center;
  line-height: 1.3;
}

.category-parent {
  font-size: 10px;
  color: #999;
  text-align: center;
  margin-top: 2px;
}

/* 表单区域 */
.form-section {
  padding: 20px 16px;
  flex: 1;
}

.form-row {
  margin-bottom: 20px;
}

.form-row label {
  display: block;
  font-size: 13px;
  color: #999;
  margin-bottom: 8px;
}

.form-select,
.form-date,
.form-input {
  width: 100%;
}

.form-select :deep(.el-input__wrapper),
.form-date :deep(.el-input__wrapper),
.form-input :deep(.el-input__wrapper) {
  border-radius: 10px;
  box-shadow: none;
  border: 1px solid #e8e8e8;
  padding: 8px 12px;
}

.form-select :deep(.el-input__wrapper):hover,
.form-date :deep(.el-input__wrapper):hover,
.form-input :deep(.el-input__wrapper):hover {
  border-color: #667eea;
}

.change-category {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  color: #667eea;
  font-size: 14px;
  cursor: pointer;
  border: 1px dashed #667eea;
  border-radius: 10px;
  margin-top: 8px;
  transition: background 0.2s;
}

.change-category:hover {
  background: rgba(102, 126, 234, 0.05);
}

/* 数字键盘 */
.number-keypad {
  background: #f8f9fa;
  padding: 12px;
  flex-shrink: 0;
  border-top: 1px solid #eee;
}

.keypad-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.key-btn {
  height: 52px;
  border: none;
  border-radius: 10px;
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.key-btn:active {
  transform: scale(0.95);
}

.key-btn.number {
  background: #fff;
  color: #333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.key-btn.number:hover {
  background: #f5f5f5;
}

.key-btn.operation {
  background: #e9ecef;
  color: #495057;
  font-size: 15px;
}

.key-btn.operation:hover {
  background: #dee2e6;
}

.key-btn.delete {
  background: #fee2e2;
  color: #dc2626;
}

.key-btn.delete:hover {
  background: #fecaca;
}

.key-btn.save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 16px;
}

.key-btn.save:hover {
  opacity: 0.9;
}

/* 桌面端适配 - 居中显示带阴影 */
@media (min-width: 500px) {
  .transaction-page {
    padding: 20px;
    align-items: center;
    background: linear-gradient(135deg, #e0e5ec 0%, #f5f7fa 100%);
  }
  
  .transaction-form {
    min-height: calc(100vh - 40px);
    max-height: 800px;
    border-radius: 24px;
    box-shadow: 
      0 25px 50px -12px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }
  
  .main-content {
    border-radius: 20px 20px 0 0;
  }
}

/* 大屏幕 - 更大的间距 */
@media (min-width: 768px) {
  .transaction-page {
    padding: 40px;
  }
  
  .transaction-form {
    max-height: 750px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 360px) {
  .category-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px 8px;
  }
  
  .category-icon {
    width: 44px;
    height: 44px;
    font-size: 20px;
  }
  
  .amount-number {
    font-size: 40px;
  }
  
  .key-btn {
    height: 48px;
    font-size: 18px;
  }
}
</style>
